/**
 * Absolute links into the app, for anywhere the destination is left behind — an
 * email body, a share sheet, a chat message.
 *
 * Kept pure and free of native imports so the URL contract can be tested without
 * booting the Expo preset (same pattern as `sso-redirect.utils`).
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * Two rules that the hand-rolled links in `ShareModal` broke, and that anything
 * building one of these has to honour:
 *
 * 1. **A route group never appears in a URL.** In-app navigation targets
 *    `/(tabs)/news/news-detail` because that is the file path; the browser URL
 *    is `/news/news-detail`. Pasting the router pathname into a link emitted a
 *    literal `(tabs)` segment.
 *
 * 2. **These screens take their id as a QUERY parameter, not a path segment.**
 *    `app/(tabs)/news/news-detail.tsx` is a static filename, not `[newsId].tsx`,
 *    and the screen reads `useLocalSearchParams<{ newsId }>()`. So
 *    `/news/news-detail/<id>` cannot supply the id at all — it has to be
 *    `?newsId=<id>`. Events are the exception: `app/(tabs)/events/[id]` IS a
 *    dynamic segment, so its id goes in the path.
 */

export type DeepLinkResource = "news" | "wiki" | "event" | "post";

type ResourceRoute = Readonly<{
  /** Browser path — route groups already stripped. */
  path: string;
  /** Query key the screen reads the id from; omitted when it is a path segment. */
  param?: string;
}>;

/**
 * Mirrors the in-app navigation in `notification-row.tsx`. If a route or a param
 * name changes there, it changes here — a link that 404s is worse than no link.
 */
const ROUTES: Record<DeepLinkResource, ResourceRoute> = {
  news: { path: "/news/news-detail", param: "newsId" },
  wiki: { path: "/wiki/wiki-article-detail", param: "articleId" },
  post: { path: "/feeds", param: "post_id" },
  // `app/(tabs)/events/[id]` — a real dynamic segment, so no query param.
  event: { path: "/events" },
};

const stripTrailingSlash = (url: string): string => url.replace(/\/+$/, "");

/**
 * An absolute link to one resource, or `""` when it cannot be built.
 *
 * Returns `""` rather than a half-formed URL on purpose: a link to
 * `https://host/news/news-detail?newsId=` lands the reader on a detail screen
 * with nothing to show, which is worse than the caller omitting the link and
 * falling back to plain text.
 */
/**
 * The path + query for one resource, with no leading slash and no origin —
 * for a template that builds the origin separately (the "NewsCreated" mail
 * template's body is literally `http://{{HostName}}/{{RedirectionUrl}}`, so
 * `RedirectionUrl` must not carry its own leading slash or the link doubles
 * one up).
 *
 * `""` when the resource/id cannot resolve, same convention as
 * `buildResourceUrl`.
 */
export const resourcePath = (
  resource: DeepLinkResource,
  resourceId: string,
): string => {
  if (!resourceId) return "";

  const route = ROUTES[resource];
  if (!route) return "";

  const path = route.param
    ? `${route.path}?${route.param}=${encodeURIComponent(resourceId)}`
    : `${route.path}/${encodeURIComponent(resourceId)}`;

  return path.replace(/^\/+/, "");
};

export const buildResourceUrl = (
  baseUrl: string,
  resource: DeepLinkResource,
  resourceId: string,
): string => {
  const origin = stripTrailingSlash(baseUrl ?? "");
  if (!origin) return "";

  const path = resourcePath(resource, resourceId);
  return path ? `${origin}/${path}` : "";
};

/**
 * Where the app is actually being served.
 *
 * On web the current origin is authoritative — the env-configured host is a
 * different deployment, so links built from it opened the wrong site (or
 * nothing). Native has no `window`, so it falls back to the configured host.
 *
 * ⚠ Anything running OUTSIDE a browser tab — a notification payload consumed by
 * an email template, for instance — must have `EXPO_PUBLIC_SHARE_URL` set, or
 * the link cannot be built at all.
 */
export const appBaseUrl = (): string => {
  if (typeof window !== "undefined" && window.location?.origin) {
    return window.location.origin;
  }
  return process.env.EXPO_PUBLIC_SHARE_URL ?? "";
};

/** Convenience wrapper — the common case, resolved against the running app. */
export const resourceUrl = (
  resource: DeepLinkResource,
  resourceId: string,
): string => buildResourceUrl(appBaseUrl(), resource, resourceId);

/**
 * Routes that are an ENTRY point rather than a destination: landing here on a
 * cold load is the login/onboarding flow doing its job, not a place to stay.
 */
const ENTRY_PATHS: readonly string[] = [
  "/",
  "/login",
  "/login-options",
  "/forgot-password",
  "/reset-password",
  "/set-password",
  "/org-select",
  "/pending",
  // The SSO callback (`app/auth/callback.tsx`). Its URL carries a single-use
  // `code` + `state`: a cold load of it used to be remembered as the intended
  // path, so the post-login redirect sent the user straight back to it and the
  // callback re-ran the exchange with an already-redeemed code and state.
  "/auth",
];

/**
 * True when a cold page load of `pathname` should STAY there instead of being
 * replaced by the redirect's default `/feeds`.
 *
 * It replaces a rule that listed the routes it recognised — the four linkable
 * resources, later plus `/profile` — which meant every other screen lost its
 * destination on a fresh load: a reload or a shared `/groups/<id>`,
 * `/settings`, `/events` … was rewritten to the feed. Listing is the trap, since
 * the next screen added needs someone to remember to add it.
 *
 * Asking "is this an app screen rather than an entry screen?" covers a route the
 * moment its file exists.
 */
export const isPreservablePath = (
  pathname: string | null | undefined,
): boolean => {
  if (!pathname) return false;
  const path = pathname.split(/[?#]/)[0].replace(/\/+$/, "") || "/";
  return !ENTRY_PATHS.some(
    (entry) => path === entry || path.startsWith(`${entry}/`),
  );
};

/**
 * Where a visitor was trying to go, for the trip through /login.
 *
 * A deep link opened without a session is bounced to `/login`, and the URL is
 * gone by the time a session exists — the router only knows about the login
 * screen, so the destination has to be carried across the sign-in explicitly.
 *
 * `sessionStorage`, not `localStorage`: per tab, gone when it closes. An intent
 * belongs to this visit, not to the device; a path remembered yesterday must not
 * hijack today's login.
 */
const INTENDED_PATH_KEY = "selise-club:intended-path";

/** Native has no `window`; a module variable carries the same visit. */
let intendedPathInMemory: string | null = null;

export const rememberIntendedPath = (path: string | null | undefined): void => {
  const intended = (path ?? "").trim();
  if (!isPreservablePath(intended)) return;
  intendedPathInMemory = intended;
  if (typeof window !== "undefined") {
    try {
      window.sessionStorage?.setItem(INTENDED_PATH_KEY, intended);
    } catch {
      // Private mode / storage disabled: the in-memory copy still carries it.
    }
  }
};

/** Read-and-clear: the intent is spent once the redirect has used it. */
export const consumeIntendedPath = (): string | null => {
  let intended = intendedPathInMemory;
  if (typeof window !== "undefined") {
    try {
      intended = window.sessionStorage?.getItem(INTENDED_PATH_KEY) ?? intended;
      window.sessionStorage?.removeItem(INTENDED_PATH_KEY);
    } catch {
      // Fall back to whatever memory holds.
    }
  }
  intendedPathInMemory = null;
  return isPreservablePath(intended) ? intended : null;
};

/** Drop it without using it — signing out ends the visit it belonged to. */
export const clearIntendedPath = (): void => {
  intendedPathInMemory = null;
  if (typeof window !== "undefined") {
    try {
      window.sessionStorage?.removeItem(INTENDED_PATH_KEY);
    } catch {
      // Nothing to clear.
    }
  }
};
